import { Box, Container, Typography } from "@mui/material";
import { useLanguage } from "../../i18n/LanguageContext";
import "./smartKeySection.css";

const icons = {
    checkin: <><rect x="4" y="7" width="16" height="13" rx="2" /><path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M4 12h16" /></>,
    access: <><rect x="7" y="6" width="10" height="12" rx="3" /><path d="m9 6 1-3h4l1 3M9 18l1 3h4l1-3M12 9.5V12l1.5 1.5" /></>,
    orders: <><path d="M4 5h16v11H9l-5 4z" /><path d="m12 7.5.9 1.6 1.6.9-1.6.9-.9 1.6-.9-1.6-1.6-.9 1.6-.9z" /></>,
    parking: <><rect x="3.5" y="3.5" width="17" height="17" rx="3" /><path d="M10 16.5v-9h3a2.5 2.5 0 0 1 0 5h-3" /></>,
    housekeeping: <><path d="M3 18V7M3 14h18v4M21 14v-2a3 3 0 0 0-3-3h-7v5" /><circle cx="7" cy="11" r="1.6" /></>,
    devices: <><rect x="3" y="4" width="8" height="7" rx="1.5" /><rect x="13" y="4" width="8" height="7" rx="1.5" /><rect x="8" y="14" width="8" height="7" rx="1.5" /></>,
    console: <><rect x="2.8" y="3.8" width="18.4" height="12.4" rx="2" /><path d="M8.5 20.2h7M12 16.2v4" /><path d="m6.5 12.5 3-3 2.5 2 5-4.5" /></>,
    lock: <><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 7.5-2M12 14.5v3" /></>,
    secure: <><path d="M12 2.8 19 5.6v5.6c0 4.6-3 8.5-7 10-4-1.5-7-5.4-7-10V5.6z" /><path d="m8.8 12 2.2 2.2 4.4-4.4" /></>,
    power: <path d="M13 2 4 14h7l-1 8 9-12h-7z" />,
    alerts: <><rect x="2.5" y="7" width="17" height="10" rx="2" /><path d="M21.5 10.5v3M11 9.5v3M11 15h.01" /></>,
};

const content = {
    en: {
        title: "Smart Key",
        intro: "Smart Key brings guest access, AI-powered service and proactive hotel operations into one connected platform, under your hotel's own brand. Guests skip the queue and walk straight to their room, while your team sees every room, request and device in real time. The result: happier guests, better reviews, lower running costs and new revenue.",
        groups: [
            {
                title: "Guest Experience",
                subtitle: "A smoother journey from arrival to check-out.",
                cards: [
                    { icon: "checkin", title: "Branded early check-in", text: "Guests check in before they arrive, in an app that carries your hotel's brand, and walk straight to their room." },
                    { icon: "access", title: "Access their way", text: "Guests open their door with their phone, a card or a smartwatch, or simply say “Open the door”. Parcels can be delivered with secure access too." },
                    { icon: "orders", title: "AI orders by text or voice", text: "Guests order room service, housekeeping or maintenance by typing or speaking, in English or Arabic. The AI sends each request to the right team and keeps the guest updated." },
                    { icon: "parking", title: "Smart parking", text: "A connected arrival from the gate to the lobby, better use of every parking space, and a new revenue stream for the hotel." },
                ],
            },
            {
                title: "Operational Efficiency",
                subtitle: "Your team sees everything in real time.",
                cards: [
                    { icon: "housekeeping", title: "Housekeeping control", text: "Every room moves from dirty to cleaning to inspected on one live board, with workflows, service-level targets and KPIs your managers can track." },
                    { icon: "devices", title: "Real-time view of every device", text: "Smart Key discovers, sets up and tracks the devices in your rooms automatically, so you know the state of every asset without walking the floors." },
                    { icon: "console", title: "One console for your staff", text: "Your team handles check-ins, room changes and guest requests from one simple screen, so a smaller team can deliver a better service." },
                ],
            },
            {
                title: "Security & Access",
                subtitle: "Modern access without starting from scratch.",
                cards: [
                    { icon: "lock", title: "Upgrade your existing locks", text: "Smart Key works with the physical locks you already have and upgrades them, instead of replacing every door." },
                    { icon: "secure", title: "Secure by design", text: "Each key works only for the right guest and only for their stay. Reception can cancel it in one click, protecting your guests and your reputation." },
                ],
            },
            {
                title: "Energy & Asset Health",
                subtitle: "Lower running costs and healthier equipment.",
                cards: [
                    { icon: "power", title: "Power-saving sensors", text: "Sensors switch off what an empty room does not need, helping cut the electricity bill by up to 40%." },
                    { icon: "alerts", title: "Fault and battery alerts", text: "Smart Key warns your team about expected faults and low batteries before a guest ever notices." },
                ],
            },
        ],
    },
    ar: {
        title: "ضياف",
        intro: "يجمع ضياف دخول الضيوف والخدمة المدعومة بالذكاء الاصطناعي والتشغيل الاستباقي للفندق في منصة واحدة متصلة، وبهوية فندقك الخاصة. يتجاوز الضيوف طابور الاستقبال ويتوجهون مباشرة إلى غرفهم، بينما يرى فريقك كل غرفة وطلب وجهاز لحظة بلحظة. والنتيجة: ضيوف أكثر رضا، وتقييمات أفضل، وتكاليف تشغيل أقل، ومصادر دخل جديدة.",
        groups: [
            {
                title: "تجربة الضيف",
                subtitle: "رحلة أكثر سلاسة من الوصول حتى المغادرة.",
                cards: [
                    { icon: "checkin", title: "تسجيل وصول مبكر بهوية فندقك", text: "يسجّل الضيوف وصولهم قبل أن يصلوا عبر تطبيق يحمل هوية فندقك، ثم يتوجهون مباشرة إلى غرفهم." },
                    { icon: "access", title: "الدخول بالطريقة التي يفضلها الضيف", text: "يفتح الضيف بابه بهاتفه أو ببطاقة أو بساعته الذكية، أو يكفي أن يقول «افتح الباب». ويمكن أيضاً منح صلاحية دخول آمنة لتسليم الطرود." },
                    { icon: "orders", title: "طلبات بالذكاء الاصطناعي نصاً أو صوتاً", text: "يطلب الضيوف خدمة الغرف أو التنظيف أو الصيانة كتابةً أو تحدثاً بالعربية أو الإنجليزية، فيرسل الذكاء الاصطناعي كل طلب إلى الفريق المختص ويُبقي الضيف على اطلاع." },
                    { icon: "parking", title: "المواقف الذكية", text: "وصول متصل من البوابة حتى بهو الفندق، واستغلال أفضل لكل موقف، ومصدر دخل جديد للفندق." },
                ],
            },
            {
                title: "الكفاءة التشغيلية",
                subtitle: "فريقك يرى كل شيء لحظة بلحظة.",
                cards: [
                    { icon: "housekeeping", title: "إدارة التدبير المنزلي", text: "تنتقل كل غرفة من «تحتاج تنظيفاً» إلى «قيد التنظيف» ثم «تم الفحص» على لوحة مباشرة واحدة، مع سير عمل ومستويات خدمة ومؤشرات أداء يتابعها مديروك." },
                    { icon: "devices", title: "رؤية فورية لكل الأجهزة", text: "يكتشف ضياف أجهزة الغرف ويهيئها ويتابعها تلقائياً، فتعرف حالة كل جهاز في كل غرفة دون جولات تفقدية." },
                    { icon: "console", title: "لوحة تحكم واحدة لفريقك", text: "يدير فريقك تسجيل الوصول وتغيير الغرف وطلبات الضيوف من شاشة واحدة بسيطة، فيقدّم فريق أصغر خدمة أفضل." },
                ],
            },
            {
                title: "الأمان والدخول",
                subtitle: "دخول عصري دون البدء من الصفر.",
                cards: [
                    { icon: "lock", title: "طوّر أقفالك الحالية", text: "يعمل ضياف مع الأقفال الموجودة لديك ويطوّرها، بدلاً من استبدال كل باب." },
                    { icon: "secure", title: "آمن من الأساس", text: "يعمل كل مفتاح للضيف الصحيح وطوال مدة إقامته فقط، ويستطيع الاستقبال إلغاءه بنقرة واحدة، حمايةً لضيوفك ولسمعة فندقك." },
                ],
            },
            {
                title: "الطاقة وسلامة الأصول",
                subtitle: "تكاليف تشغيل أقل وأجهزة بحالة أفضل.",
                cards: [
                    { icon: "power", title: "مستشعرات توفير الطاقة", text: "تطفئ المستشعرات ما لا تحتاجه الغرفة الشاغرة، مما يساعد على خفض فاتورة الكهرباء بنسبة تصل إلى 40%." },
                    { icon: "alerts", title: "تنبيهات الأعطال والبطاريات", text: "ينبّه ضياف فريقك إلى الأعطال المتوقعة وانخفاض شحن البطاريات قبل أن يلاحظها أي ضيف." },
                ],
            },
        ],
    },
};

const SmartKeySection = () => {
    const { lang } = useLanguage();
    const c = content[lang];
    return (
        <>
            <Container maxWidth="lg" align="center" className="smartKeySection" id="smartkeySection">
                <Typography variant="h3" gutterBottom>
                    {c.title}
                </Typography>
                <Typography variant="subtitle1" className="smartKeyIntro">
                    {c.intro}
                </Typography>
                {c.groups.map((group) => (
                    <Box className="smartKeyGroup" key={group.title}>
                        <Box className="smartKeyGroupHead">
                            <Typography variant="h4">{group.title}</Typography>
                            <Typography>{group.subtitle}</Typography>
                        </Box>
                        <Box className="smartKeyGrid">
                            {group.cards.map((card) => (
                                <Box className="smartKeyCard" key={card.title}>
                                    <Box className="smartKeyCardIcon">
                                        <svg viewBox="0 0 24 24" aria-hidden="true">{icons[card.icon]}</svg>
                                    </Box>
                                    <Typography variant="h5">{card.title}</Typography>
                                    <Typography>{card.text}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ))}
            </Container>
        </>
    )
}
export default SmartKeySection;
